import { RedirectCommand, Router, Routes, UrlTree } from '@angular/router';
import { OnboardingRouteType } from '../onboarding/models/onboarding-route-type';
import { LocationsStore } from '../locations/store/locations.store';
import { EnergyInterfacesStore } from '../energy-interfaces/stores/energy-interfaces.store';
import { SupplierContractStore } from '../supplier-contracts/stores/supplier-contracts.store';
import { ParseRequestsStore } from '../parse-requests/stores/parse-requests.store';
import { SupplierContractForecastStore } from '../supplier-contracts/stores/supplier-contract-forecast.store';
import { DocumentsStore } from '../documents/stores/documents.store';
import { ConsumptionStore } from '../analysis/stores/consumption.store';
import { organizationLoadedGuard } from './guards/organization-loaded.guard';
import { DASHBOARD_ROUTES } from '../dashboard/dashboard.routes';
import { LOCATIONS_ROUTES } from '../locations/locations.routes';
import { ENERGY_INTERFACES_ROUTES } from '../energy-interfaces/energy-interfaces.routes';
import { SUPPLIER_CONTRACTS_ROUTES } from '../supplier-contracts/supplier-contracts.routes';
import { PARSE_REQUESTS_ROUTES } from '../parse-requests/parse-requests.routes';
import { permissionGuard } from './guards/permission.guard';
import { DOCUMENTS_ROUTES } from '../documents/documents.routes';
import { ANALYSIS_ROUTES } from '../analysis/analysis.routes';
import { SETTINGS_ROUTES } from '../settings/settings.routes';
import { inject } from '@angular/core';
import { OrganizationsStore } from './store/organizations.store';
import { OrganizationsPage } from './pages/organizations-page/organizations-page';
import { SupplierContractWizardStore } from '../supplier-contracts/stores/supplier-contract-wizard.store';

export const ORGANIZATION_ROUTES: Routes = [
  {
    path: 'onboarding',
    data: {
      type: 'USER_ONBOARDING' as OnboardingRouteType,
    },
    loadChildren: () => import('../onboarding/onboarding.routes').then((m) => m.ONBOARDING_ROUTES),
  },
  {
    path: 'create',
    data: {
      type: 'CREATE_ORGANIZATION' as OnboardingRouteType,
    },
    loadChildren: () => import('../onboarding/onboarding.routes').then((m) => m.ONBOARDING_ROUTES),
  },
  {
    path: ':organizationId',
    providers: [
      LocationsStore,
      EnergyInterfacesStore,
      SupplierContractStore,
      SupplierContractWizardStore,
      ParseRequestsStore,
      SupplierContractForecastStore,
      DocumentsStore,
      ConsumptionStore,
    ],
    canActivate: [organizationLoadedGuard],
    children: [
      {
        path: 'onboarding',
        data: {
          type: 'USER_ONBOARDING' as OnboardingRouteType,
        },
        loadChildren: () =>
          import('../onboarding/onboarding.routes').then((m) => m.ONBOARDING_ORGA_ROUTES),
      },
      {
        path: 'create',
        data: {
          type: 'CREATE_ORGANIZATION' as OnboardingRouteType,
        },
        loadChildren: () =>
          import('../onboarding/onboarding.routes').then((m) => m.ONBOARDING_ORGA_ROUTES),
      },
      {
        path: 'dashboard',
        children: DASHBOARD_ROUTES,
      },
      {
        path: 'locations',
        children: LOCATIONS_ROUTES,
      },
      {
        path: 'energy-interfaces',
        children: ENERGY_INTERFACES_ROUTES,
      },
      {
        path: 'supplier-contracts',
        children: SUPPLIER_CONTRACTS_ROUTES,
      },
      {
        path: 'parse-requests',
        children: PARSE_REQUESTS_ROUTES,
      },
      {
        path: 'documents',
        canActivate: [permissionGuard(['ORGANIZATION_MEMBERSHIP'])],
        children: DOCUMENTS_ROUTES,
      },
      {
        path: 'analysis',
        canActivate: [permissionGuard(['ORGANIZATION_MEMBERSHIP'])],
        children: ANALYSIS_ROUTES,
      },
      {
        path: 'settings',
        canActivate: [
          permissionGuard(['ORGANIZATION_INVITE_USER', 'ORGANIZATION_MASTERDATA_WRITE']),
        ],
        children: SETTINGS_ROUTES,
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '',
    pathMatch: 'full',
    canActivate: [
      // onboarding guard
      () => {
        const store = inject(OrganizationsStore);
        // if orga fetch was not triggered yet, trigger it
        if (!store.allLoaded() && !store.allLoading() && !store.allLoadingError()) {
          // trigger loading without waiting for the result to display loading states in the ui
          store.loadAll();
          return true; // let the route activate, we will redirect later in the OrganizationsPage if needed
        }
        // if loading is done and there are no organizations, redirect to onboarding
        if (store.allLoaded() && store.entitiesList().length === 0) {
          const urlTree: UrlTree = inject(Router).parseUrl('/organizations/onboarding');
          return new RedirectCommand(urlTree);
        }
        // edge case: if there was an error loading organizations, or the loading is still in progress,
        // let the route activate, we will redirect later in the OrganizationsPage if needed
        // todo: implement this in the organizations page component
        return true;
      },
    ],
    component: OrganizationsPage,
  },
  {
    path: '**',
    loadComponent: () =>
      import('../layout/pages/not-found-page/not-found-page').then((m) => m.NotFoundPage),
  },
];
